import {
  ArrowLeftIcon,
  FireIcon,
  HomeIcon,
  MapPinIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Avatar,
  Flex,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import { PhotoList } from '@/features/work/components/photo-list/PhotoList';
import { VideoList } from '@/features/work/components/video-list/VideoList';
import { WorkHistoryList } from '@/features/work/components/work-history-list/WorkHistoryList';
import { useArtistConditionView } from '@/features/work/view/artist-full-profile/hooks/useArtistConditionView';
import { PageLoading } from '@/pages/PageLoading';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBModalFullscreenLayout } from '@/shared/layouts/modal/ModalFullScreenLayout';
import {
  LBBadge,
  LBListContainer,
  LBListItem,
  LBNavLink
} from '@/shared/ui-kit/components';

export const ArtistFullProfile = ({
  artistProfileId
}: {
  artistProfileId: string;
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { isLoading, fullProfile, profilePhoto }
  } = useArtistConditionView({ profileId: artistProfileId });
  const [photoModalStatus, { open: openPhotoModal, close: closePhotoModal }] =
    useDisclosure();
  const [videoModalStatus, { open: openVideoModal, close: closeVideoModal }] =
    useDisclosure();
  const [
    experienceModalStatus,
    { open: openExperienceModal, close: closeExperienceModal }
  ] = useDisclosure();

  if (isLoading) return <PageLoading />;

  return (
    <div>
      <LBModalFullscreenLayout
        opened={photoModalStatus}
        onClose={closePhotoModal}
      >
        <Flex>
          <ActionIcon
            color='gray'
            variant='transparent'
            onClick={closePhotoModal}
          >
            <ArrowLeftIcon width={24} />
          </ActionIcon>
        </Flex>
        <Space h={32} />
        <Title>{translate('commons.photo')}</Title>
        <Space h={32} />
        {fullProfile.data?.data.photos && (
          <PhotoList data={fullProfile.data?.data.photos} />
        )}
      </LBModalFullscreenLayout>
      <LBModalFullscreenLayout
        opened={videoModalStatus}
        onClose={closeVideoModal}
      >
        <Flex>
          <ActionIcon
            color='gray'
            variant='transparent'
            onClick={closeVideoModal}
          >
            <ArrowLeftIcon width={24} />
          </ActionIcon>
        </Flex>
        <Space h={32} />
        <Title>{translate('commons.video')}</Title>
        <Space h={32} />
        {fullProfile.data?.data.videos.length && (
          <VideoList data={fullProfile.data?.data.videos} />
        )}
      </LBModalFullscreenLayout>
      <LBModalFullscreenLayout
        opened={experienceModalStatus}
        onClose={closeExperienceModal}
      >
        <Flex>
          <ActionIcon
            color='gray'
            variant='transparent'
            onClick={closeExperienceModal}
          >
            <ArrowLeftIcon width={24} />
          </ActionIcon>
        </Flex>
        <Space h={32} />
        <Title>{translate('commons.experience')}</Title>
        <Space h={32} />
        {fullProfile.data?.data.workExperience.workHistory.length ? (
          <WorkHistoryList
            data={fullProfile.data.data.workExperience.workHistory}
          />
        ) : (
          <Text>{translate('commons.withouthExprience')}</Text>
        )}
      </LBModalFullscreenLayout>

      <Flex align='center' gap={16}>
        <Avatar
          size={120}
          src={
            profilePhoto.data?.data[0]?.filePath &&
            resolveImagePath(profilePhoto.data.data[0]?.filePath)
          }
        />
        <Flex direction='column' gap={8}>
          <Flex direction='column'>
            <Title order={3}>{fullProfile.data?.data.info?.firstName}</Title>
            <Text fz={14}>
              {dayjs().diff(fullProfile.data?.data.info?.birthDate, 'years')}{' '}
              лет
            </Text>
          </Flex>
          <Flex>
            <MapPinIcon width={18} />
            <Text fz={14}>
              {fullProfile.data?.data.info?.countryOfResidence}
            </Text>
          </Flex>
          <LBBadge>
            {fullProfile.data?.data.status && (
              <FormattedMessage
                id={`commons.artistStatuses.${fullProfile.data?.data.status}`}
              />
            )}
          </LBBadge>
        </Flex>
      </Flex>
      <Space h={16} />
      <Stack gap={16}>
        <Stack gap={4}>
          <LBNavLink
            label={translate('commons.photo')}
            leftSection={<PhotoIcon width={18} />}
            tt='capitalize'
            onClick={openPhotoModal}
          />
          <LBNavLink
            label={translate('commons.video')}
            leftSection={<HomeIcon width={18} />}
            tt='capitalize'
            onClick={openVideoModal}
          />
          <LBNavLink
            label={translate('commons.experience')}
            leftSection={<FireIcon width={18} />}
            tt='capitalize'
            onClick={openExperienceModal}
          />
        </Stack>
        <Stack gap={4}>
          <LBListContainer
            label={formatMessage({ id: 'commons.wantedVacancy' })}
          >
            {fullProfile.data?.data.info?.isLookingForStripVacancy && (
              <LBListItem>
                <Text fz={14}>
                  {translate('commons.artistJobs.Strip')}{' '}
                  {translate('commons.dancer')}
                </Text>
              </LBListItem>
            )}
            {fullProfile.data?.data.info?.isLookingForGoGoVacancy && (
              <LBListItem>
                <Text fz={14}>
                  {translate('commons.artistJobs.GoGo')}{' '}
                  {translate('commons.dancer')}
                </Text>
              </LBListItem>
            )}
            {fullProfile.data?.data.info?.isLookingForHostessVacancy && (
              <LBListItem>
                <Text fz={14}>{translate('commons.artistJobs.Hostess')}</Text>
              </LBListItem>
            )}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.citizenship' })}>
            {fullProfile.data?.data.info?.citizenships?.map((item) => (
              <LBListItem key={item}>
                <Text fz={14}>{item}</Text>
              </LBListItem>
            ))}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.language' })}>
            {fullProfile.data?.data.languages?.map((item) => (
              <LBListItem key={item.name}>
                <Text fz={14}>{item.name}</Text>
                <Text fz={14}>
                  {translate(`commons.languages.${item.level}`)}
                </Text>
              </LBListItem>
            ))}
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.parametres' })}>
            <LBListItem>
              <Text fz={14}>
                {translate('commons.artistParametres.height')}
              </Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.height} см
              </Text>
            </LBListItem>
            <LBListItem>
              <Text fz={14}>
                {translate('commons.artistParametres.weight')}
              </Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.weight} кг
              </Text>
            </LBListItem>
            <LBListItem>
              <Text fz={14}>{translate('commons.artistParametres.body')}</Text>
              <Text fz={14}>
                {fullProfile.data?.data.parameters?.chest} /
                {fullProfile.data?.data.parameters?.waist} /
                {fullProfile.data?.data.parameters?.hips} см
              </Text>
            </LBListItem>
          </LBListContainer>
          <LBListContainer label={formatMessage({ id: 'commons.about' })}>
            <Text fz={14} style={{ whiteSpace: 'pre-wrap' }}>
              {fullProfile.data?.data?.about}
            </Text>
          </LBListContainer>
        </Stack>
      </Stack>
    </div>
  );
};
